import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../../firebase/AuthProvider';
import { toast } from 'react-toastify';
import { GetParticipantResourceByIdAsync, UpdateParticipantResourceAsync } from '../../../services/ParticipantResourceService/ParticipantResouceService';
import { AxiosError } from 'axios';
import Loader from '../loader/Loader';
import { EditParticipantResourceForm, HoursField, UpdateParticipantResourceDto } from '../../../interfaces/participantResource/participantResource';
import { DAYS, ParticipantResourceStatus, ParticipantResourceStatusOptions } from '../../../Data/SD';
import { to24Hour } from '../../../utilities/common/commonFuntion';
import { ErrorState } from '../../../utilities/common/commonModal';
import { Region } from '../../../interfaces/identity/Region';
import { getAllRegions } from '../../../services/userServices/UserInfoService';
import Select, { SingleValue } from 'react-select';

const EditParticipantResource = () => {
    const { participantResourceId } = useParams<{ participantResourceId: string }>();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState<EditParticipantResourceForm>();
    const [regions, setRegions] = useState<Region[]>([]);
    const [hoursFields, setHoursFields] = useState<HoursField[]>([]);
    const [errors, setErrors] = useState<ErrorState>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        fetchResource();
        getRegions();
        // eslint-disable-next-line
    }, [participantResourceId, currentUser]);

    const getRegions = async () => {
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        try {
            const res = await getAllRegions(token);
            if (res.success) {
                setRegions(res.data);
            }
        } catch (e) {
            toast.error("Failed to load regions");
        }

    };




    const fetchResource = async () => {
        try {
            const token = await currentUser?.getIdToken();
            if (!token || !participantResourceId) return;
            const res = await GetParticipantResourceByIdAsync(token, participantResourceId);
            if (res.success) {
                setForm({
                    Title: res.data.title,
                    Description: res.data.description,
                    PhoneNumber: res.data.phoneNumber,
                    AddressLine1: res.data.addressLine1,
                    AddressLine2: res.data.addressLine2,
                    City: res.data.city,
                    State: res.data.state,
                    PostalCode: res.data.postalCode,
                    RegionId: res.data.regionId,
                    Status: res.data.status,
                    HoursNotes: res.data.hoursNotes,
                    Notes: res.data.notes
                });
                const hoursObj = res.data.hoursJsonb ? JSON.parse(res.data.hoursJsonb) : {};
                const hoursArr: HoursField[] = Object.entries(hoursObj).map(([key, value]) => {
                    const [startDay, endDay] = key.split('-');
                    const [startTimeRaw, endTimeRaw] = (value as string).split('-');
                    return {
                        startDay: startDay || '',
                        endDay: endDay || '',
                        startTime: to24Hour(startTimeRaw || ''),
                        endTime: to24Hour(endTimeRaw || '')
                    };
                });
                setHoursFields(hoursArr);
            } else {
                toast.error(res.message || "Resource not found");
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to saving the Resource");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        if (!form) return;
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: undefined });
    };

    const handleHoursChange = (index: number, field: keyof HoursField, value: string) => {
        const updated = [...hoursFields];
        updated[index][field] = value;
        setHoursFields(updated);
        setErrors({ ...errors, [`HoursJsonb.${index}.${field}`]: undefined });
    };

    const addHours = () =>
        setHoursFields([...hoursFields, { startDay: '', endDay: '', startTime: '', endTime: '' }]);
    const removeHours = (i: number) =>
        setHoursFields(hoursFields.filter((_, idx) => idx !== i));

    const validate = (): ErrorState => {
        const e: ErrorState = {};
        if (!form) return e;
        Object.entries(form).forEach(([k, v]) => {
            if (!v) e[k] = `${k} is required.`;
        });
        if (form.PhoneNumber && !/^\+?[0-9\s\-]{7,15}$/.test(form.PhoneNumber))
            e.PhoneNumber = 'Phone number format is invalid.';
        if (form.PostalCode && !/^\d{4,10}$/.test(form.PostalCode))
            e.PostalCode = 'Postal code format is invalid.';
        if (form.Status && !['Accepting', 'WaitlistOnly', 'NotAccepting'].includes(form.Status))
            e.Status = 'Invalid status.';
        if (hoursFields.length === 0) {
            e.HoursJsonb = 'At least one hours entry is required.';
        }
        hoursFields.forEach((item, idx) => {
            if (!item.startDay) e[`HoursJsonb.${idx}.startDay`] = 'Start day required.';
            if (item.endDay && DAYS.indexOf(item.endDay) <= DAYS.indexOf(item.startDay))
                e[`HoursJsonb.${idx}.endDay`] = 'End day must be after start day.';
            if (!item.startTime) e[`HoursJsonb.${idx}.startTime`] = 'Start time required.';
            if (!item.endTime) e[`HoursJsonb.${idx}.endTime`] = 'End time required.';
            if (item.startTime && item.endTime && item.startTime >= item.endTime)
                e[`HoursJsonb.${idx}.endTime`] = 'End time must be after start time.';
        });
        return e;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const v = validate();
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        const hoursObj: Record<string, string> = {};
        hoursFields.forEach(h => {
            const key = h.endDay ? `${h.startDay}-${h.endDay}` : h.startDay;
            hoursObj[key] = `${h.startTime}-${h.endTime}`;
        });

        if (!form || !participantResourceId) return;
        const finalData: UpdateParticipantResourceDto = {
            id: participantResourceId,
            title: form.Title,
            description: form.Description,
            phoneNumber: form.PhoneNumber,
            addressLine1: form.AddressLine1,
            addressLine2: form.AddressLine2,
            city: form.City,
            state: form.State,
            postalCode: form.PostalCode,
            regionId: form.RegionId,
            status: form.Status,
            hoursNotes: form.HoursNotes,
            notes: form.Notes,
            hoursJsonb: JSON.stringify(hoursObj)
        };

        // Submit logic here (uncomment and implement as needed)
        try {
            setIsSaving(true);
            const token = await currentUser?.getIdToken();
            if (!token) return;
            const res = await UpdateParticipantResourceAsync(token, finalData);
            if (res.success) {
                toast.success(res.message);
                navigate("/dashboard/owner/resources");
            } else {
                toast.error(res.message || "Update failed");
            }
        } catch (error: unknown) {
            const err = error as AxiosError;
            const errorMessage = (err.response?.data as any)?.message || err.message || "Error updating resource";
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const regionOptions = regions.map(region => ({
        value: region.regionId,
        label: region.regionName
    }));

        const handleRegionChange = (selected: SingleValue<{ value: number; label: string }>) => {
            if (!form) return;
            setForm({ ...form, RegionId: selected ? Number(selected.value) : 0 });
            setErrors({ ...errors, RegionId: undefined });
        };


    return (

        <div className="common_card">
            {isLoading && <Loader />}
            <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                    <h2>Edit Participant Resource</h2>
                </div>
            </div>
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Title</Form.Label>
                            <Form.Control name="Title" value={form?.Title} onChange={handleChange} placeholder='Enter Title..' />
                            {errors.Title && <div className="text-danger">{errors.Title}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control name="Description" value={form?.Description} onChange={handleChange} placeholder='Enter Description..' />
                            {errors.Description && <div className="text-danger">{errors.Description}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control name="PhoneNumber" value={form?.PhoneNumber} onChange={handleChange} placeholder='Enter PhoneNumber..' />
                            {errors.PhoneNumber && <div className="text-danger">{errors.PhoneNumber}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Address Line 1</Form.Label>
                            <Form.Control name="AddressLine1" value={form?.AddressLine1} onChange={handleChange} placeholder='Enter Address Line 1..' />
                            {errors.AddressLine1 && <div className="text-danger">{errors.AddressLine1}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Address Line 2</Form.Label>
                            <Form.Control name="AddressLine2" value={form?.AddressLine2} onChange={handleChange} placeholder='Enter Address Line 2..' />
                            {errors.AddressLine2 && <div className="text-danger">{errors.AddressLine2}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>City</Form.Label>
                            <Form.Control name="City" value={form?.City} onChange={handleChange} placeholder='Enter City..' />
                            {errors.City && <div className="text-danger">{errors.City}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>State</Form.Label>
                            <Form.Control name="State" value={form?.State} onChange={handleChange} placeholder='Enter State..' />
                            {errors.State && <div className="text-danger">{errors.State}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Postal Code</Form.Label>
                            <Form.Control name="PostalCode" value={form?.PostalCode} onChange={handleChange} placeholder='Enter Postal Code..' />
                            {errors.PostalCode && <div className="text-danger">{errors.PostalCode}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Region</Form.Label>
                            <Select
                                options={regionOptions}
                                value={regionOptions.find(opt => opt.value === Number(form?.RegionId))}
                                onChange={handleRegionChange}
                                isClearable
                                classNamePrefix="react-select"
                                placeholder="Select Region"
                                name="RegionId"
                            />
                            {errors.RegionId && <p className="text-red-500 text-sm mt-1">{errors.RegionId}</p>}
                            {errors.RegionId && <div className="text-danger">{errors.RegionId}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select name="Status" value={form?.Status} onChange={handleChange}>
                                <option value="">Select Status</option>
                                {ParticipantResourceStatusOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </Form.Select>
                            {errors.Status && <div className="text-danger">{errors.Status}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Hours Notes</Form.Label>
                            <Form.Control name="HoursNotes" value={form?.HoursNotes} onChange={handleChange} placeholder='Enter Hours Notes..' />
                            {errors.HoursNotes && <div className="text-danger">{errors.HoursNotes}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Additional Notes</Form.Label>
                            <Form.Control name="Notes" value={form?.Notes} onChange={handleChange} placeholder='Enter Additional Notes..' />
                            {errors.Notes && <div className="text-danger">{errors.Notes}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                <hr />
                <h5>Hours (edit or add multiple)</h5>
                {errors.HoursJsonb && (
                    <p className="text-red-500 text-sm mt-1">{errors.HoursJsonb}</p>
                )}
                {hoursFields.map((f, i) => (
                    <Row key={i}>
                        <Col md={2}>
                            <Form.Select value={f.startDay} onChange={e => handleHoursChange(i, 'startDay', e.target.value)}>
                                <option value="">Start Day</option>
                                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                            </Form.Select>
                            {errors[`HoursJsonb.${i}.startDay`] && <div className="text-danger">{errors[`HoursJsonb.${i}.startDay`]}</div>}
                        </Col>
                        <Col md={2}>
                            <Form.Select value={f.endDay} onChange={e => handleHoursChange(i, 'endDay', e.target.value)}>
                                <option value="">End Day (optional)</option>
                                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                            </Form.Select>
                            {errors[`HoursJsonb.${i}.endDay`] && <div className="text-danger">{errors[`HoursJsonb.${i}.endDay`]}</div>}
                        </Col>
                        <Col md={2}>
                            <Form.Control
                                type="time"
                                value={f.startTime}
                                onChange={e => handleHoursChange(i, 'startTime', e.target.value)}
                                placeholder="Start Time"
                            />
                            {errors[`HoursJsonb.${i}.startTime`] && <div className="text-danger">{errors[`HoursJsonb.${i}.startTime`]}</div>}
                        </Col>
                        <Col md={2}>
                            <Form.Control
                                type="time"
                                value={f.endTime}
                                onChange={e => handleHoursChange(i, 'endTime', e.target.value)}
                                placeholder="End Time"
                            />
                            {errors[`HoursJsonb.${i}.endTime`] && <div className="text-danger">{errors[`HoursJsonb.${i}.endTime`]}</div>}
                        </Col>
                        <Col md={2}>
                            <Button variant="danger" onClick={() => removeHours(i)}>Remove</Button>
                        </Col>
                    </Row>
                ))}
                <Button className="mt-2" variant="secondary" onClick={addHours}>
                    Add Hours
                </Button>
                <hr />
                <div className="form_btn_flx mby_t_24">
                    <Button type="submit" variant="primary" disabled={isSaving}>
                        {isSaving ? <span><Spinner size='sm' /> Updating...</span> : "Update Resource"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default EditParticipantResource;